import { useEffect, useState } from "react";
import { Button } from "../../../../components/buttons/button";
import { SearchInput } from "../../../../components/inputs/searchInput";
import { ProgressBar } from "../../../../components/progressBar";
import { GuestsTable } from "../../../../components/table/app_tables/guestsTable";
import { MiniHeader } from "../../../../components/typography/miniHeader";
import { BgWrapper } from "../../../../components/wrapper/bgWrapper";
import { useAdminGetEventGuestsQuery } from "../../../../redux/features/admin/event/events.api";
import { useGetEventGuestsQuery } from "../../../../redux/features/event/event.api";
import {
  CreatedEvent,
  GetEventGuestsResponse,
} from "../../../../redux/features/event/event.types";
import { QueryParams, TicketSalesBreakdown } from "../../../../utils/types";

export const EventGuests = ({
  event,
  ticketBreakdown,
  adminView,
}: {
  event: CreatedEvent;
  ticketBreakdown: TicketSalesBreakdown;
  adminView: boolean;
}) => {
  const [guests, setGuests] = useState<GetEventGuestsResponse>();
  const [fetchingGuests, setFetchingGuests] = useState<boolean>(false);

  const [queryParams, setQueryParams] = useState<
    QueryParams & {
      search: string;
      sort: "asc" | "desc";
    }
  >({
    search: "",
    sort: "desc",
    page: 1,
    limit: 10,
  });

  const { data: clientGuests, isFetching: clientFetchingGuests } =
    useGetEventGuestsQuery(
      {
        id: event._id,
        search: queryParams.search,
        page: queryParams.page,
        limit: queryParams.limit,
        sortOrder: queryParams.sort,
      },
      { refetchOnMountOrArgChange: true, skip: adminView },
    );

  const { data: adminGuests, isFetching: adminnFetchingGuests } =
    useAdminGetEventGuestsQuery(
      {
        id: event._id,
        search: queryParams.search,
        page: queryParams.page,
        limit: queryParams.limit,
        sortOrder: queryParams.sort,
      },
      { refetchOnMountOrArgChange: true, skip: !adminView },
    );

  useEffect(() => {
    if (adminView) {
      setFetchingGuests(adminnFetchingGuests);
      setGuests(adminGuests);
    } else {
      setFetchingGuests(clientFetchingGuests);
      setGuests(clientGuests);
    }
  }, [
    clientGuests,
    clientFetchingGuests,
    adminGuests,
    adminnFetchingGuests,
    adminView,
  ]);

  return (
    <>
      <BgWrapper className="flex flex-col gap-3 !rounded-t-none">
        <MiniHeader header="Guest Insight" />

        <p className="text-2xl font-medium tracking-[-0.04em] text-grey-600">
          {guests?.data?.totalCount ?? 0} guests
        </p>

        <ProgressBar
          step={ticketBreakdown.map((tb) => tb.purchaseQuantity)}
          className="!h-[8px]"
          colors={ticketBreakdown.map((tb) => tb.color.split(",")[0])}
        />

        <div className="mt-3 flex flex-col gap-3">
          <MiniHeader header="Guest List" />

          <div className="flex w-full flex-col items-center justify-between gap-4 md:flex-row">
            <SearchInput
              searchFunction={(value) => {
                setQueryParams({
                  ...queryParams,
                  search: value,
                  page: 1,
                });
              }}
              placeholder="Search"
              className="md:!max-w-[300px]"
            />

            <div className="grid w-full grid-cols-2 items-center gap-3 md:flex md:w-fit md:flex-row">
              <Button
                type="button"
                onClick={() => {
                  if (queryParams.sort === "asc") {
                    setQueryParams({ ...queryParams, sort: "desc" });
                  } else {
                    setQueryParams({ ...queryParams, sort: "asc" });
                  }
                }}
                text="Register Time"
                className="!min-w-fit !rounded-2xl !p-3 !text-sm"
                icon={queryParams.sort === "asc" ? "order-asc" : "order-desc"}
                variation="grey"
              />

              <Button
                type="button"
                onClick={() => {}}
                text="Export"
                className="!min-w-fit !rounded-2xl !p-3 !text-sm"
                icon="export"
                variation="grey"
              />
            </div>
          </div>

          <GuestsTable
            guests={guests?.data?.data ?? []}
            loading={fetchingGuests}
            showTimeAgo={true}
            isPaginated
            currentPage={queryParams.page}
            totalCount={guests?.data?.totalCount ?? 0}
            itemsPerPage={queryParams.limit}
            onPageChange={(page) => {
              setQueryParams({
                ...queryParams,
                page: page,
              });
            }}
            ticketColor={ticketBreakdown.map((tb) => {
              return {
                ticketId: tb.ticketId,
                color: tb.color,
              };
            })}
            adminView={adminView}
          />
        </div>
      </BgWrapper>
    </>
  );
};
